import { tokenStorage } from './tokenStorage';
import { loginService } from './loginService';

/**
 * Why a session was not usable. Worth distinguishing because the three cases
 * have completely different causes: nobody ever logged in on this machine, a
 * real 30-day expiry, or credentials that are half-present (timestamp but no
 * token), which points at a storage problem rather than at the user.
 */
export type AuthFailureReason = 'never-logged-in' | 'session-expired' | 'token-missing';

export interface AuthSessionState {
  isAuthenticated: boolean;
  token: string | null;
  // True when this check just logged out an idle OTP session.
  loggedOutForInactivity?: boolean;
  /** Present only when isAuthenticated is false. */
  reason?: AuthFailureReason;
}

export async function getValidAuthSession(): Promise<AuthSessionState> {
  // Read BEFORE enforceIdleTimeout() and isSessionValid(): both can clear the
  // stored credentials, after which the failure cases are indistinguishable.
  const hadLogin = Boolean(await tokenStorage.getLoginTimestamp());
  const loggedOutForInactivity = await loginService.enforceIdleTimeout();
  const sessionValid = await tokenStorage.isSessionValid();
  const token = sessionValid ? await tokenStorage.getAuthToken() : null;
  const isAuthenticated = Boolean(sessionValid && token);

  let reason: AuthFailureReason | undefined;
  if (!isAuthenticated) {
    if (!hadLogin) reason = 'never-logged-in';
    else if (!sessionValid) reason = 'session-expired';
    else reason = 'token-missing';
  }

  return { isAuthenticated, token, loggedOutForInactivity, reason };
}

export async function requireValidAuthSession(onUnauthenticated?: () => void): Promise<AuthSessionState> {
  const session = await getValidAuthSession();

  if (!session.isAuthenticated) {
    onUnauthenticated?.();
  }

  return session;
}
